import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocalizedCurrency } from '../localization/LocalizedComponents';

interface Creator {
  id: string;
  userId: string;
  displayName: string;
  avatar: string | null;
}

interface CommissionFormData {
  creatorId: string;
  title: string;
  description: string;
  amount: number;
  currency: string;
  deadline?: string;
  requirements?: string;
}

export const CommissionRequestForm: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [creators, setCreators] = useState<Creator[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CommissionFormData>({
    creatorId: '',
    title: '',
    description: '',
    amount: 0,
    currency: 'USD',
    deadline: '',
    requirements: ''
  });

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchCreators();
    }
  }, [searchQuery]);

  const searchCreators = async () => {
    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(searchQuery)}&type=creator&limit=10`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setCreators(data.creators || []);
    } catch (error) {
      console.error('Failed to search creators:', error);
    }
  };

  const handleSelectCreator = (creator: Creator) => {
    setSelectedCreator(creator);
    setFormData({ ...formData, creatorId: creator.userId });
    setSearchQuery('');
    setCreators([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.creatorId || !formData.title || !formData.description || formData.amount <= 0) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/commissions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        alert('Commission request sent successfully!');
        navigate(`/commissions/${data.commission.id}`);
      } else {
        alert(data.error || 'Failed to create commission request');
      }
    } catch (error) {
      console.error('Failed to create commission:', error);
      alert('Failed to create commission request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Request a Commission</h1>
        <p className="text-gray-600">Work directly with creators on custom projects</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Creator Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Creator <span className="text-red-500">*</span>
          </label>
          
          {selectedCreator ? (
            <div className="flex items-center justify-between bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                {selectedCreator.avatar && (
                  <img
                    src={selectedCreator.avatar}
                    alt={selectedCreator.displayName}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900">{selectedCreator.displayName}</p>
                  <p className="text-sm text-gray-600">Selected Creator</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedCreator(null);
                  setFormData({ ...formData, creatorId: '' });
                }}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a creator..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              
              {creators.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {creators.map((creator) => (
                    <button
                      key={creator.id}
                      type="button"
                      onClick={() => handleSelectCreator(creator)}
                      className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 transition-colors text-left"
                    >
                      {creator.avatar && (
                        <img
                          src={creator.avatar}
                          alt={creator.displayName}
                          className="w-10 h-10 rounded-full"
                        />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{creator.displayName}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <p className="text-sm text-gray-500 mt-1">Search for the creator you want to commission</p>
        </div>

        {/* Commission Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Commission Title <span className="text-red-500">*</span>
          </label>
          <input
            id="title"
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., Custom Character Illustration"
            maxLength={200}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe what you want commissioned in detail..."
            rows={6}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          />
          <p className="text-sm text-gray-500 mt-1">Be as detailed as possible about what you want</p>
        </div>

        {/* Budget & Currency */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-2">
              Budget <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500">$</span>
              <input
                id="amount"
                type="number"
                min="1"
                step="0.01"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                required
                className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <select
              id="currency"
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="CAD">CAD ($)</option>
              <option value="AUD">AUD ($)</option>
            </select>
          </div>
        </div>

        {/* Deadline */}
        <div>
          <label htmlFor="deadline" className="block text-sm font-medium text-gray-700 mb-2">
            Deadline (Optional)
          </label>
          <input
            id="deadline"
            type="date"
            value={formData.deadline}
            onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
          <p className="text-sm text-gray-500 mt-1">When do you need this completed?</p>
        </div>

        {/* Additional Requirements */}
        <div>
          <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Requirements (Optional)
          </label>
          <textarea
            id="requirements"
            value={formData.requirements}
            onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
            placeholder="Any specific requirements, file formats, revisions included, etc..."
            rows={4}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">How commissions work:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Your request is sent to the creator for review</li>
                <li>If accepted, payment is held in escrow for security</li>
                <li>The creator delivers the work based on your requirements</li>
                <li>You review and approve to release payment to the creator</li>
                <li>A 5% platform fee applies to all commissions</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            {t('actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading || !formData.creatorId || !formData.title || !formData.description || formData.amount <= 0}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-600 text-white rounded-lg font-medium hover:shadow-lg transition-shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending Request...' : 'Send Commission Request'}
          </button>
        </div>
      </form>
    </div>
  );
};
